import { PageableSearchResult, SearchResult } from "@scrile/api-provider/dist/api/types";
import {
  ContentFeed,
  ContentFeedSearchResult,
  InputContentFeedSearch,
  InputDiscoveryContentFeedSearch,
  InputFollowingContentFeedSearch,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import providers from "@/lib/api";

interface PageableSearchInput {
  page?: number;
}

const iterativelyFindFeedItem = async <
  T extends PageableSearchInput,
  R extends PageableSearchResult & SearchResult<ContentFeed>
>(
  feedIdToFind: string,
  data: T,
  searchFunc: (payload: { data: T }, fields?: string) => Promise<R>,
  fields?: string
): Promise<R> => {
  let resp = await searchFunc.call(providers.ContentFeedProvider, { data }, fields);
  while (resp.result.findIndex(i => i.id === feedIdToFind) === -1 && resp.hasNextPage) {
    data.page = (data.page ?? 0) + 1;
    const currentResult = resp.result;
    resp = await searchFunc.call(providers.ContentFeedProvider, { data }, fields);
    resp.result = currentResult.concat(resp.result);
  }
  return resp;
};

export default function useContentFeed() {
  const contentFeedSearch = async (data: InputContentFeedSearch, feedIdToFind?: string, fields?: string) => {
    let response: ContentFeedSearchResult;
    if (feedIdToFind) {
      response = await iterativelyFindFeedItem(feedIdToFind, data, providers.ContentFeedProvider.search, fields);
    } else response = await providers.ContentFeedProvider.search({ data }, fields);
    return response;
  };

  const contentFeedFollowingSearch = async (
    data: InputFollowingContentFeedSearch,
    feedIdToFind?: string,
    fields?: string
  ) => {
    let response: ContentFeedSearchResult;
    if (feedIdToFind) {
      response = await iterativelyFindFeedItem(
        feedIdToFind,
        data,
        providers.ContentFeedProvider.followingContentFeedSearch,
        fields
      );
    } else response = await providers.ContentFeedProvider.followingContentFeedSearch({ data }, fields);
    return response;
  };

  const contentFeedDiscoverySearch = async (
    data: InputDiscoveryContentFeedSearch,
    feedIdToFind?: string,
    fields?: string
  ) => {
    let response: ContentFeedSearchResult;
    if (feedIdToFind) {
      response = await iterativelyFindFeedItem(
        feedIdToFind,
        data,
        providers.ContentFeedProvider.discoveryContentFeedSearch,
        fields
      );
    } else response = await providers.ContentFeedProvider.discoveryContentFeedSearch({ data }, fields);
    return response;
  };

  const contentFeedDeletePost = (id: string) => {
    return providers.ContentSetProvider.delete({ id });
  };
  const contentFeedStatsGetCount = async (userId: string) => {
    const resp = await providers.ContentSetProvider.getStatistic({ userIds: [userId] });
    return resp[userId];
  };

  return {
    contentFeedSearch,
    contentFeedFollowingSearch,
    contentFeedDiscoverySearch,
    contentFeedDeletePost,
    contentFeedStatsGetCount,
  };
}
